import { useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";
import { fmtMeters } from "@/features/ze_tenders/utils";
import { DEADLINE_BUCKETS, type MegamapProject, type UiFilters } from "./data";
import type { AggregateResult, Zone } from "./clustering";
import { COLOR_DIMS, legendFor, SIZE_DIMS, type ColorDim, type SizeDim } from "./colors";

const card = "rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-800";
const label = "text-xs font-medium text-gray-500 dark:text-gray-400";
const sel = "mt-0.5 w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900";

export interface Options {
  statuses: { value: string; label: string }[];
  clients: { value: string; label: string }[];
  coordinators: { value: string; label: string }[];
  branches: { value: string; label: string }[];
  projects: { value: string; label: string }[];
}

/** Derive stable filter option lists from the full (unfiltered) dataset. */
export function buildOptions(rows: MegamapProject[]): Options {
  const uniq = (pairs: [string, string][]) => {
    const m = new Map<string, string>();
    for (const [v, l] of pairs) if (v) m.set(v, l);
    return [...m.entries()].map(([value, l]) => ({ value, label: l })).sort((a, b) => a.label.localeCompare(b.label, "pl"));
  };
  const coordPairs: [string, string][] = [];
  for (const r of rows) r.coordinator_ids.forEach((id, i) => coordPairs.push([id, r.coordinator_names[i] ?? id]));
  const st: Record<string, string> = { active: "Aktywny", suspended: "Wstrzymany", inactive: "Zamknięty" };
  return {
    statuses: uniq(rows.map((r) => [r.status, st[r.status] ?? r.status])),
    clients: uniq(rows.map((r) => [r.client_id ?? "", r.client_name ?? ""])),
    coordinators: uniq(coordPairs),
    branches: uniq(rows.map((r) => [r.branch_id ?? "", r.branch_name ?? ""])),
    projects: uniq(rows.map((r) => [r.id, r.name])),
  };
}

/** Lightweight inline-expanding multiselect (checkbox list) — no popover so it never clips
 * inside the scrolling side panel. Empty selection = "all". */
function MultiSelect({ options, selected, onChange, allLabel }: {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label ?? "1 wybrano"
        : `${selected.length} wybrano`;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${sel} flex items-center justify-between gap-1 text-left`}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown size={14} className={`shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 max-h-44 space-y-0.5 overflow-y-auto rounded-md border border-gray-200 bg-white p-1 dark:border-gray-600 dark:bg-gray-900">
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full rounded px-1.5 py-0.5 text-left text-[11px] text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              wyczyść ({selected.length})
            </button>
          )}
          {options.map((o) => (
            <label key={o.value} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-0.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800">
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** Searchable checkbox picker where every option is ticked by default. State is the set of
 * *excluded* ids (empty = all shown), so unticking hides a project from the map. */
function ProjectPicker({ options, excluded, onChange, allLabel }: {
  options: { value: string; label: string }[];
  excluded: string[];
  onChange: (next: string[]) => void;
  allLabel: string;
}) {
  const RENDER_CAP = 200;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const exSet = useMemo(() => new Set(excluded), [excluded]);
  const checkedCount = Math.max(0, options.length - excluded.length);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  }, [options, q]);
  const capped = shown.slice(0, RENDER_CAP);

  const toggle = (v: string) =>
    onChange(exSet.has(v) ? excluded.filter((x) => x !== v) : [...excluded, v]);
  const checkShown = () => {
    const s = new Set(shown.map((o) => o.value));
    onChange(excluded.filter((v) => !s.has(v)));
  };
  const uncheckShown = () => {
    const s = new Set(excluded);
    for (const o of shown) s.add(o.value);
    onChange([...s]);
  };

  const summary = excluded.length === 0 ? allLabel : `${checkedCount} / ${options.length} projektów`;
  const scope = q.trim() ? "widoczne" : "wszystkie";

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${sel} flex items-center justify-between gap-1 text-left`}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown size={14} className={`shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 rounded-md border border-gray-200 bg-white p-1 dark:border-gray-600 dark:bg-gray-900">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Szukaj projektu…"
              className="w-full rounded border border-gray-200 bg-white py-1 pl-7 pr-2 text-xs dark:border-gray-600 dark:bg-gray-800"
            />
          </div>
          <div className="mt-1 flex items-center justify-between px-0.5 text-[11px] text-gray-500">
            <button type="button" onClick={checkShown} className="rounded px-1 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-800">
              zaznacz {scope}
            </button>
            <button type="button" onClick={uncheckShown} className="rounded px-1 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-800">
              odznacz {scope}
            </button>
          </div>
          <div className="mt-1 max-h-60 space-y-0.5 overflow-y-auto">
            {capped.map((o) => (
              <label key={o.value} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-0.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800">
                <input type="checkbox" checked={!exSet.has(o.value)} onChange={() => toggle(o.value)} />
                <span className="truncate">{o.label}</span>
              </label>
            ))}
            {shown.length === 0 && <div className="px-1.5 py-1 text-[11px] text-gray-400">Brak dopasowań</div>}
            {shown.length > RENDER_CAP && (
              <div className="px-1.5 py-1 text-[11px] text-gray-400">
                …i jeszcze {shown.length - RENDER_CAP} — zawęź wyszukiwaniem
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function FiltersPanel({ f, onChange, options, total, shown }: {
  f: UiFilters;
  onChange: (next: UiFilters) => void;
  options: Options;
  total: number;
  shown: number;
}) {
  const set = (patch: Partial<UiFilters>) => onChange({ ...f, ...patch });
  return (
    <div className={card}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-semibold">Filtry</h3>
        <span className="text-xs text-gray-500">{shown} / {total}</span>
      </div>
      <button
        type="button"
        onClick={() => set({ needsSubcontractor: !f.needsSubcontractor })}
        className={`mb-2 w-full rounded-md px-2 py-1.5 text-left text-xs font-medium transition-colors ${
          f.needsSubcontractor ? "bg-red-600 text-white" : "bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300"
        }`}
        title="Prywatne działki nieuzgodnione i brak zamówienia WPW"
      >
        ⚑ Do przydziału podwykonawcy
      </button>
      <label className="mb-1.5 flex items-center gap-2 text-xs">
        <input type="checkbox" checked={f.hasUnagreedPrivate} onChange={(e) => set({ hasUnagreedPrivate: e.target.checked })} />
        Ma nieuzgodnione działki prywatne
      </label>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className={label}>Zamówienie WPW</div>
          <select className={sel} value={f.wpw} onChange={(e) => set({ wpw: e.target.value as UiFilters["wpw"] })}>
            <option value="all">Wszystkie</option>
            <option value="with">Ma WPW</option>
            <option value="without">Bez WPW</option>
          </select>
        </div>
        <div>
          <div className={label}>Status</div>
          <select className={sel} value={f.status} onChange={(e) => set({ status: e.target.value })}>
            <option value="">Wszystkie</option>
            {options.statuses.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <div className={label}>Klient</div>
          <MultiSelect
            options={options.clients}
            selected={f.clients}
            onChange={(v) => set({ clients: v })}
            allLabel="Wszyscy"
          />
        </div>
        <div className="col-span-2">
          <div className={label}>Koordynator</div>
          <MultiSelect
            options={[{ value: "unassigned", label: "Nieprzypisany" }, ...options.coordinators]}
            selected={f.coordinators}
            onChange={(v) => set({ coordinators: v })}
            allLabel="Wszyscy"
          />
        </div>
        <div className="col-span-2">
          <div className={label}>Oddział</div>
          <MultiSelect
            options={options.branches}
            selected={f.branches}
            onChange={(v) => set({ branches: v })}
            allLabel="Wszystkie"
          />
        </div>
        <div className="col-span-2">
          <div className={label}>Termin</div>
          <select className={sel} value={f.deadline} onChange={(e) => set({ deadline: e.target.value as UiFilters["deadline"] })}>
            {DEADLINE_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <div className={label}>Projekty</div>
          <ProjectPicker
            options={options.projects}
            excluded={f.projectsExcluded}
            onChange={(v) => set({ projectsExcluded: v })}
            allLabel="Wszystkie projekty"
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-gray-500">{k}</span>
      <span className="font-semibold tabular-nums">{v}</span>
    </div>
  );
}

export function RollupPanel({ title, agg }: { title: string; agg: AggregateResult }) {
  return (
    <div className={card}>
      <h3 className="mb-2 font-semibold">{title}</h3>
      <div className="space-y-1">
        <Stat k="Projekty" v={String(agg.count)} />
        <Stat k="Trasy" v={fmtMeters(agg.route_length_m)} />
        <Stat k="Działki" v={String(agg.parcel_count)} />
        <Stat k="Działki pryw. nieuzg." v={String(agg.private_unagreed_parcels)} />
        <Stat k="WPW niewysłane w teren" v={String(agg.private_owners_pending)} />
        <Stat k="Wartość" v={`${formatAmount(agg.total_value)} zł`} />
        <Stat k="Do przydziału podwyk." v={String(agg.needs_subcontractor)} />
      </div>
    </div>
  );
}

export function ColorByPanel({ dim, onDim, sizeDim, onSizeDim, rows }: {
  dim: ColorDim;
  onDim: (d: ColorDim) => void;
  sizeDim: SizeDim;
  onSizeDim: (d: SizeDim) => void;
  rows: MegamapProject[];
}) {
  const entries = legendFor(dim, rows);
  return (
    <div className={card}>
      <div className={label}>Koloruj wg</div>
      <select className={sel} value={dim} onChange={(e) => onDim(e.target.value as ColorDim)}>
        {COLOR_DIMS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
      </select>
      <div className={`${label} mt-2`}>Rozmiar kółka wg</div>
      <select className={sel} value={sizeDim} onChange={(e) => onSizeDim(e.target.value as SizeDim)}>
        {SIZE_DIMS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
      </select>
      <div className="mt-2 space-y-1">
        {entries.map((e) => (
          <div key={e.label} className="flex items-center justify-between gap-2 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: e.color }} />
              {e.label}
            </span>
            <span className="tabular-nums text-gray-500">{e.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MapLayersPanel({ voivodeshipLines, onVoivodeshipLines }: {
  voivodeshipLines: boolean;
  onVoivodeshipLines: (on: boolean) => void;
}) {
  return (
    <div className={card}>
      <div className={label}>Warstwy mapy</div>
      <label className="mt-1 flex cursor-pointer items-center gap-2 text-xs">
        <input type="checkbox" checked={voivodeshipLines} onChange={(e) => onVoivodeshipLines(e.target.checked)} />
        Granice województw
      </label>
    </div>
  );
}

export function ZonesPanel({ zones, onZoom, thresholdKm }: {
  zones: Zone[];
  onZoom: (z: Zone) => void;
  thresholdKm: number;
}) {
  const multi = zones.filter((z) => z.items.length > 1);
  return (
    <div className={card}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-semibold">Strefy bliskości</h3>
        <span className="text-xs text-gray-500" title="Promień skupiania rośnie przy oddaleniu, maleje przy przybliżeniu">
          ~{thresholdKm} km · wg zoomu
        </span>
      </div>
      {multi.length === 0 ? (
        <p className="text-xs text-gray-500">Brak skupisk przy tym przybliżeniu — oddal mapę.</p>
      ) : (
        <div className="max-h-64 space-y-2 overflow-y-auto">
          {multi.slice(0, 12).map((z, i) => (
            <button key={z.id} type="button" onClick={() => onZoom(z)} className="w-full rounded-md border border-gray-100 p-2 text-left text-xs hover:border-red-300 hover:bg-red-50/40 dark:border-gray-700 dark:hover:bg-gray-700/40">
              <div className="flex items-center justify-between font-medium">
                <span>Strefa {i + 1} · {z.items.length} proj.</span>
                <span className="tabular-nums text-gray-500">{fmtMeters(z.items.reduce((a, p) => a + (p.route_length_m || 0), 0))}</span>
              </div>
              <div className="mt-0.5 text-gray-500">
                {z.needsCount > 0 && <span className="text-red-600">{z.needsCount} do przydziału · </span>}
                {z.hasWpwCount}/{z.items.length} z podwyk.
              </div>
              {z.topCoordinator && <div className="text-gray-500">Kandydat na PM: <b>{z.topCoordinator}</b></div>}
              {z.sharedContractors.length > 0 && <div className="text-gray-500">Podwyk. w pobliżu: {z.sharedContractors.join(", ")}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function SelectionPanel({ items, agg, onClear }: { items: MegamapProject[]; agg: AggregateResult; onClear: () => void }) {
  if (items.length === 0) return null;
  const coordFreq = new Map<string, number>();
  for (const it of items) it.coordinator_names.forEach((n) => coordFreq.set(n, (coordFreq.get(n) ?? 0) + 1));
  const topCoord = [...coordFreq.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const contractors = [...new Set(items.flatMap((i) => i.wpw_contractor_names))];
  return (
    <div className={`${card} ring-1 ring-red-300`}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-semibold">Zaznaczenie</h3>
        <button type="button" onClick={onClear} className="text-xs text-gray-500 hover:text-red-600">wyczyść</button>
      </div>
      <div className="space-y-1">
        <Stat k="Projekty" v={String(agg.count)} />
        <Stat k="Trasy" v={fmtMeters(agg.route_length_m)} />
        <Stat k="Działki pryw. nieuzg." v={String(agg.private_unagreed_parcels)} />
        <Stat k="WPW niewysłane w teren" v={String(agg.private_owners_pending)} />
        <Stat k="Wartość" v={`${formatAmount(agg.total_value)} zł`} />
        <Stat k="Do przydziału podwyk." v={String(agg.needs_subcontractor)} />
      </div>
      {topCoord && <p className="mt-2 text-xs text-gray-500">Najczęstszy koordynator: <b>{topCoord}</b></p>}
      {contractors.length > 0 && <p className="text-xs text-gray-500">Podwykonawcy WPW: {contractors.join(", ")}</p>}
    </div>
  );
}
